import { useCallback, useEffect, useState } from 'react';
import * as orgApi from '../../api/orgApi';
import { useAuth } from '../../auth/AuthProvider';

export function OrgInvitationsPage() {
  const { session } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  const isAdmin = session?.membershipRole === 'admin' || session?.isPlatformAdmin;

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await orgApi.listInvitations();
      setInvitations(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load invitations');
      setInvitations([]);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <p className="text-sm text-eorig-muted">
        Organization admin role is required to manage invitations.
      </p>
    );
  }

  async function handleCreate(event) {
    event.preventDefault();
    setError(null);
    setLastInviteUrl(null);
    try {
      const result = await orgApi.createInvitation({ email, role });
      setLastInviteUrl(result.invite_url || null);
      setEmail('');
      await load();
    } catch (err) {
      setError(err.message || 'Invite failed');
    }
  }

  async function handleResend(id) {
    setError(null);
    setLastInviteUrl(null);
    try {
      const result = await orgApi.resendInvitation(id);
      setLastInviteUrl(result.invite_url || null);
    } catch (err) {
      setError(err.message || 'Resend failed');
    }
  }

  async function handleRevoke(id) {
    setError(null);
    try {
      await orgApi.revokeInvitation(id);
      await load();
    } catch (err) {
      setError(err.message || 'Revoke failed');
    }
  }

  return (
    <div className="space-y-6">
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <form
        onSubmit={handleCreate}
        className="grid gap-3 rounded-2xl border border-eorig-border bg-white p-4 sm:grid-cols-2"
      >
        <h2 className="sm:col-span-2 text-sm font-semibold">Invite teammate</h2>
        <input
          required
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          <option value="admin">admin</option>
          <option value="member">member</option>
          <option value="viewer">viewer</option>
        </select>
        <button
          type="submit"
          className="rounded-xl bg-umb-navy px-4 py-2 text-sm font-semibold text-white sm:col-span-2"
        >
          Create invite
        </button>
        {lastInviteUrl ? (
          <p className="sm:col-span-2 break-all text-xs text-eorig-muted">
            Invite URL: {lastInviteUrl}
          </p>
        ) : null}
      </form>

      <div className="overflow-hidden rounded-2xl border border-eorig-border bg-white">
        {invitations.length === 0 ? (
          <p className="p-4 text-sm text-eorig-muted">No invitations.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-eorig-border bg-eorig-surface text-xs uppercase text-eorig-muted">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((invite) => (
                <tr key={invite.id} className="border-b border-eorig-border/70">
                  <td className="px-4 py-3">{invite.email}</td>
                  <td className="px-4 py-3">{invite.role}</td>
                  <td className="px-4 py-3">{invite.status}</td>
                  <td className="px-4 py-3">
                    {invite.status === 'PENDING' ? (
                      <span className="flex gap-3">
                        <button
                          type="button"
                          className="text-xs font-semibold text-eorig-navy-600"
                          onClick={() => handleResend(invite.id)}
                        >
                          Resend
                        </button>
                        <button
                          type="button"
                          className="text-xs font-semibold text-red-600"
                          onClick={() => handleRevoke(invite.id)}
                        >
                          Revoke
                        </button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
